import {getUrlFromRef,uploadAndSave, updateRecord,  useStaging, typesenseApiKey, typesenseHost, uploadAndSave2 } from './firebase.js'
import { cloudflareCDN } from 'ui'
import { PhysicalObjectInterface } from '../ts/app_interfaces';
import { produce } from 'immer';
import { MaterialData, MaterialFiles, MaterialProperties, MeshTypes, ProductInterface, ProductType, GLBMesh, ProfileInterface, ProfileFirstActions, ObjectStatusType, canonicalizeMaterialCategory } from '../../../../packages/ts-interfaces/index.js';
// NEVER import three here — util.ts is eagerly reachable from App.jsx and a
// static three import drags ~190KB gz into the entry chunk. Three-dependent
// utils live in ./util3d (do not re-export them from here).
import { trackEvent, trackMetaActivationOnce } from './analyticsManager';
import { useMattoState, usePhysicalObjects } from '../MattoState';
import { isMaterialEditable } from './objectEditability';
import { trackGroupAction } from './multiSelectAnalytics';
import { productRefFromRecord, recordBoardUse } from './studioLibraryUse';

export const materialObjectType = {
    Carpet: 'Object-53dof6w9mq',
    Concrete: 'hardEdgeRectangleThin100x200_tjsfm8k8eu',
    'Engineered Surface': 'hardEdgeSquareThin200x200_hzj9ywedam',
    Fabric: 'rxm7u7h7o2',
    Flooring: 'hardEdgeSquareThin200x200_hzj9ywedam',
    Glass: 'hardEdgeSquareThin100x100_iq2ekbwe23',
    Laminate: 'hardEdgeSquareThin200x200_hzj9ywedam',
    Leather: 'rxm7u7h7o2',
    Marble: 'hardEdgeSquareThin125x125_1jn0epigoz',
    'Marble - Granite': 'hardEdgeSquareThin125x125_1jn0epigoz',
    Metal: 'hardEdgeSquareThin125x125_1jn0epigoz',
    Organic: 'hardEdgeRectangleThin150x125_qoizb6jpjf',
    Other: 'hardEdgeRectangleThin100x200_tjsfm8k8eu',
    Paint: 'paintBlob_hegyb7rce7',
    PaintProd: 'paintBlob_40yq9ndgad',
    'Paint/Plaster/Ceramic': 'hardEdgeSquareThin125x125_1jn0epigoz',
    Plastic: 'softEdgeTile100x200_h5qlejgxz6',
    Stone: 'hardEdgeSquareThin125x125_1jn0epigoz',
    Terrazzo: 'hardEdgeSquareThin125x125_1jn0epigoz',
    Tile: 'hardEdgeSquareThin100x100_iq2ekbwe23',
    Vinyl: 'hardEdgeSquareThin200x200_hzj9ywedam',
    Wallcovering: 'Object-0x60vce6cf',
    Wallpaper: 'Object-0x60vce6cf',
    'Wall Mural': 'udbrosroqf',
    Wood: 'hardEdgeSquareThin200x200_hzj9ywedam',
    Worktop: 'hardEdgeSquareThin125x125_1jn0epigoz',
    // Main Category aliases onto the same meshes as their legacy equivalents;
    // purely additive so catalog products resolve as before.
    'Fabric & Textile': 'rxm7u7h7o2',
    'Stone & Masonry': 'hardEdgeSquareThin125x125_1jn0epigoz',
    'Wallpaper & Wallcovering': 'Object-0x60vce6cf',
    Plaster: 'hardEdgeSquareThin125x125_1jn0epigoz',
    Panel: 'hardEdgeSquareThin200x200_hzj9ywedam',
    'Trim & Moulding': 'hardEdgeSquareThin200x200_hzj9ywedam',
    'Stone & Tile': 'hardEdgeSquareThin125x125_1jn0epigoz',
    Surface: 'hardEdgeSquareThin200x200_hzj9ywedam',
};

/**
 * Material object variants for random selection during board generation
 * Each category can have multiple 3D object templates that will be randomly chosen
 * to add visual variety to generated boards
 */
export const materialObjectVariants = {
    Carpet: [
        'Object-53dof6w9mq',
        'Object-000165',
        'Object-40kf8usogy'
    ],
    Concrete: [
        'hardEdgeRectangleThin100x200_tjsfm8k8eu',
        'Object-35mb7uj6lf',
        'Object-ynmi88tn31',
        'Object-sl43xpjs68',
        'Object-000260',
        'Object-42lx8p9hvx'
    ],
    'Engineered Surface': [
        'hardEdgeSquareThin200x200_hzj9ywedam',
        'Object-gag10skouj',
        'Object-me1w7v6613',
        'a05ht9slsx',
        'Object-rqyifemhlo',
        'Object-mifu6va6x8'
    ],
    Fabric: [
        'rxm7u7h7o2', 
        'Object-40kf8usogy',
        'Object-000141',
        'Object-uw2tbqy90u',
        'Object-000130'
    ],
    Flooring: [
        'hardEdgeSquareThin200x200_hzj9ywedam',
        'Object-gag10skouj',
        'Object-me1w7v6613',
        'a05ht9slsx',
        'Object-rqyifemhlo',
        'Object-mifu6va6x8'
    ],
    Glass: [
        'hardEdgeSquareThin100x100_iq2ekbwe23',
        'Object-fy5vfpu50k',
        'Object-000260',
        'romanTile_lnuseylc32'
    ], 
    Laminate: [
        'hardEdgeSquareThin200x200_hzj9ywedam',
        'Object-gag10skouj',
        'Object-me1w7v6613',
        'a05ht9slsx',
        'Object-rqyifemhlo',
        'Object-mifu6va6x8'
    ], 
    Leather: [
        'rxm7u7h7o2', 
        'Object-40kf8usogy',
        'Object-000141',
        'Object-uw2tbqy90u',
        'Object-000130'
    ],
    Marble: [
        'hardEdgeRectangleThin100x200_tjsfm8k8eu',
        'Object-35mb7uj6lf',
        'Object-ynmi88tn31',
        'Object-sl43xpjs68',
        'Object-000260',
        'Object-42lx8p9hvx'
    ],
    'Marble - Granite': [
        'hardEdgeRectangleThin100x200_tjsfm8k8eu',
        'Object-35mb7uj6lf',
        'Object-ynmi88tn31',
        'Object-sl43xpjs68',
        'Object-000260',
        'Object-42lx8p9hvx'
    ],
    Metal: [
        'hardEdgeSquareThin125x125_1jn0epigoz', 
        'Object-35mb7uj6lf',
        'Object-w70haj61em',
        'Object-fy5vfpu50k',
        'Object-42lx8p9hvx'
    ],
    Organic: [
        'hardEdgeRectangleThin150x125_qoizb6jpjf'
    ],
    Other: [
        'hardEdgeRectangleThin100x200_tjsfm8k8eu',
        'hardEdgeRectangleThin150x150_variant2'
    ],
    Paint: [
        'paintBlob_hegyb7rce7',
        'Accessory-01514',
        'Accessory-00137',
        'Accessory-01057',
        'Accessory-01056'
    ],
    PaintProd: [
        'paintBlob_hegyb7rce7',
        'Accessory-01514',
        'Accessory-00137',
        'Accessory-01057',
        'Accessory-01056'
    ],  
    'Paint/Plaster/Ceramic': [
        'Object-000145', 
        'hardEdgeSquareThin125x125_1jn0epigoz',
        'Object-0nu2piaywh',
        'Object-onanq5ye8n',
        'Object-rqyifemhlo',
        'Object-000260'
    ],
    Plastic: [
        'hardEdgeSquareThin200x200_hzj9ywedam',
        'Object-gag10skouj',
        'Object-me1w7v6613',
        'a05ht9slsx',
        'Object-rqyifemhlo',
        'Object-mifu6va6x8'
    ],
    Stone: [
        'hardEdgeRectangleThin100x200_tjsfm8k8eu',
        'Object-35mb7uj6lf',
        'Object-ynmi88tn31',
        'Object-sl43xpjs68',
        'Object-000260',
        'Object-42lx8p9hvx'
    ],
    Terrazzo: [
        'hardEdgeRectangleThin100x200_tjsfm8k8eu',
        'Object-35mb7uj6lf',
        'Object-ynmi88tn31',
        'Object-sl43xpjs68',
        'Object-000260',
        'Object-42lx8p9hvx'
    ],
    Tile: [
        'hardEdgeSquareThin100x100_iq2ekbwe23', 
        'x3hnhavncw',
        'i1rmhdh75z',
        '70vlum9f9e',
        'ak0co8ne9c',
        'fxsgah4bg7'
        ],
    Vinyl: [
        'hardEdgeSquareThin200x200_hzj9ywedam',
        'Object-gag10skouj',
        'Object-me1w7v6613',
        'a05ht9slsx',
        'Object-rqyifemhlo',
        'Object-mifu6va6x8'
    ], 
    Wallcovering: [
        'Object-0x60vce6cf', 
        'Object-000027',
        'Object-000169',
        'Object-000172'
    ],
    Wallpaper: [
        'Object-0x60vce6cf', 
        'Object-000027',
        'Object-000169',
        'Object-000172'
    ],
    'Wall Mural': [
        'udbrosroqf'
    ],
    Wood: [
        'hardEdgeSquareThin200x200_hzj9ywedam',
        'Object-gag10skouj',
        'Object-me1w7v6613',
        'a05ht9slsx',
        'Object-rqyifemhlo',
        'Object-mifu6va6x8'
    ], 
    Worktop: [
        'hardEdgeRectangleThin100x200_tjsfm8k8eu',
        'Object-35mb7uj6lf',
        'Object-ynmi88tn31',
        'Object-sl43xpjs68',
        'Object-000260',
        'Object-42lx8p9hvx'
    ]
};

// 'Paint' is the staging paint blob, 'PaintProd' the production one — every path
// resolving a 'Paint' category must go through this. Idempotent on 'PaintProd'.
export const resolvePaintObjectKey = (key: string): string =>
    key === 'Paint' && !useStaging ? 'PaintProd' : key;

// Category -> materialObjectType/materialObjectVariants key. Raw key first so
// catalog values keep their mesh; the canonical key rescues legacy values like
// 'wallpaper_matte' that title-case to nothing and used to land on Tile.
export const resolveMaterialObjectKey = (category: any): string => {
    if (!category) return 'Tile';
    const raw = resolvePaintObjectKey(capitalizeWords(String(category)));
    if (materialObjectType[raw]) return raw;
    const canonical = canonicalizeMaterialCategory(String(category));
    const canonicalKey = canonical ? resolvePaintObjectKey(canonical) : null;
    return canonicalKey && materialObjectType[canonicalKey] ? canonicalKey : 'Tile';
};

export const getRandomObjectIdForCategory = (category: string): string => {
    const key = resolvePaintObjectKey(category);
    const variants = materialObjectVariants[key];

    // If we have variants for this category, pick one randomly
    if (variants && variants.length > 0) {
        const randomIndex = Math.floor(Math.random() * variants.length);
        return variants[randomIndex];
    }

    // Fallback to old single-value system for backward compatibility
    return materialObjectType[key] || materialObjectType['Other'];
};

/**
 * List of 3D mesh objects that CAN be scaled to match material aspect ratios
 * Any object NOT in this list will preserve its original shape (safe default).
 */
export const scalableObjectIds: string[] = [
    'Object-53dof6w9mq',
    'hardEdgeRectangleThin100x200_tjsfm8k8eu',
    'hardEdgeSquareThin200x200_hzj9ywedam',
    'rxm7u7h7o2',
    'hardEdgeSquareThin125x125_1jn0epigoz',
    'hardEdgeRectangleThin150x125_qoizb6jpjf',
    'softEdgeTile100x200_h5qlejgxz6',
    'hardEdgeSquareThin100x100_iq2ekbwe23',
    'Object-0x60vce6cf',
    'udbrosroqf',
];

/**
 * Check if a mesh object should be scaled to match material aspect ratio
 * Returns true only if the mesh ID is in the scalable objects list
 */
export const shouldScaleToMaterialAspectRatio = (meshId: string): boolean => {
    if (!meshId) return false;
    return scalableObjectIds.includes(meshId);
};


export const capitalize = (s) => { if (typeof s !== 'string') return ''; return s.charAt(0).toUpperCase() + s.slice(1); }
export const capitalizeWords = (str: string): string => { return str.replace(/\b\w/g, char => char.toUpperCase());}


export const getBlobFromURL = (url) => {
    return new Promise((resolve,reject) => {
        if (url instanceof Blob == true || url instanceof File ==true) { resolve(url as Blob) }
        fetch(url).then( (response) => {
            response.blob().then( (blob) => {
                resolve(blob as Blob)
            })
        })
    })
}


// cloudflareCDN is now imported from 'ui' package
export { cloudflareCDN } from 'ui'

export const getResizedImage = (product: any, size = 400): string | null => {
  const format = "format=auto,width=" + size + ",height=" + size;
  if (product?.mesh?.rendered_image) {
    return cloudflareCDN(product?.mesh?.rendered_image, format);
  } else if (product?.materialData?.files?.color_original) {
    const u = getUrlFromRef(product?.materialData?.files?.color_original);
    if (u.endsWith('.png') || u.endsWith('.jpg') || u.endsWith('.jpeg') || u.endsWith('.webp')) {
      return cloudflareCDN(u, format);
    }
    else if (u?.endsWith('color_original')) {
      return cloudflareCDN(getUrlFromRef(product?.materialData?.files?.small_color), format);
    }
    else return u;      
  } else {
    let renderedImage =
      product?.materialData?.renderedImage ?? product?.materialData?.files?.color_original;

    if (renderedImage && renderedImage.endsWith('color_original')) {
      renderedImage = product?.materialData?.files?.small_color;
    }

    if (renderedImage) {
      const u = getUrlFromRef(renderedImage, true);
      if (u.endsWith('.png') || u.endsWith('.jpg') || u.endsWith('.jpeg') || u.endsWith('.webp')) {
        return cloudflareCDN(u, format);
      }
      else return u;    
    }
    else return null;
  }
};


export const parseSearch = (searchString='') => {
	const searchObject = {};
	const queries = searchString.replace(/^\?/, '').split('&');
	for (let i = 0; i < queries.length; i++) {
		const split = queries[i].split('=');
		searchObject[split[0]] = split[1];
	}
	return searchObject;
}

export async function updateProfile(profile:ProfileInterface,pic={}, firebaseRef) {
    return new Promise((resolve, reject) => {
        if (!profile.id) throw new Error("Profile ID is required")
        if (profile.uid?.length < 4) throw new Error("Profile UID is required")
        if (profile.email?.length < 4) throw new Error("Profile Email is required")
        const updatedProfile = produce(profile, (draft:ProfileInterface) => {
            draft.updated_at = new Date()
        })
        const unfrozenProfile = JSON.parse(JSON.stringify(updatedProfile))
        //unfortunate hack here.  uploadAndSave cannot support frozen data structures... better to do the fix here than there
        if (unfrozenProfile.createdAt && updatedProfile.createdAt) unfrozenProfile['createdAt'] = updatedProfile.createdAt
        if (unfrozenProfile.updated_at && updatedProfile.updated_at) unfrozenProfile['updatedAt'] = updatedProfile.updated_at
        if (unfrozenProfile.created_at && updatedProfile.created_at) unfrozenProfile['created_at'] = updatedProfile.created_at

        uploadAndSave2(firebaseRef,unfrozenProfile.id, unfrozenProfile.uid, unfrozenProfile, pic)
            .then(result=>{
            resolve(unfrozenProfile);
            })
            .catch(error=> { reject(error); console.log("error ", error); })
        })
  }



export const camelize = (text='') => {
	    text = text.replace(/[-_\s./]+(.)?/g, (_, c) => c ? c.toUpperCase() : '');
	    return text.substr(0, 1).toLowerCase() + text.substr(1);
}
export const createCanvas = (w,h) => { const i:any=document.createElement("canvas"); i.height=h;i.width=w; i.ctx=i.getContext('2d');return i; }
export const generateID = () =>  { return Array.from(Array(10), () => Math.floor(Math.random() * 36).toString(36)).join(''); }

// "Contact Us" Google Form. Entry ids come from the form's pre-filled-link view;
// they are shared by the prefilled viewform link below and the direct
// formResponse post in contact/ContactSales.tsx, so a form edit is fixed here once.
export const CONTACT_FORM_ID = '1FAIpQLSfWFoQ5pcpzU9ZGMsYYpzL6u7hOOZ5127_NZb__oz8TuMpSCw'
export const CONTACT_FORM_ENTRY = { name: 'entry.923666355', email: 'entry.69172660', subject: 'entry.1548136914', message: 'entry.774836436' }

export const getContactForm = (profile) => {
	if (profile?.email?.length > 2) {
		const email = profile.email || ''
		const fname = profile.first_name || ''
		const lname = profile.last_name || ''
		const name = fname + ' ' + lname
		return `https://docs.google.com/forms/d/e/${CONTACT_FORM_ID}/viewform?usp=pp_url&${CONTACT_FORM_ENTRY.email}=${email}&${CONTACT_FORM_ENTRY.name}=${name}`
	}
	else {
		return `https://docs.google.com/forms/d/e/${CONTACT_FORM_ID}/viewform`
	}
}

export const getFeedbackForm = (profile) => {
    if (profile?.email?.length > 2) {
		const email = profile.email || ''
		const fname = profile.first_name || ''
		const lname = profile.last_name || ''
		const name = fname + ' ' + lname
		return `https://docs.google.com/forms/d/e/1FAIpQLSda3Kyf-ilPQS4YjS3DTf_72ezs6BZTUL3tF19bT9INsTjV1w/viewform?usp=pp_url&entry.2084839851=${email}`
	}
	else {
		return 'https://docs.google.com/forms/d/e/1FAIpQLSda3Kyf-ilPQS4YjS3DTf_72ezs6BZTUL3tF19bT9INsTjV1w/viewform'
	}
}


/*
Sadly, got some ugly data structures here.  Let's clean it up as we move through the pipeline.
*/

const gltfMeshKeys=['category','id','name','isStaticObject','meshTextureRepeat', 'optimal_object_category', 'mode',
'rotation','scale','objectStatus','version','files','mesh','url','physicalObject','definedObject','type', 'metadata', 'productType', 'productLink', 'supplier']
export const cleanGLTFMesh = (gltfRawData={}) => {
    // Return the falsy input (not a truthy {}) so callers' null-checks fire (#3809).
    if (!gltfRawData) return gltfRawData
    const gltfMesh = {}
    Object.keys(gltfRawData).forEach(key=>{
        if(gltfMeshKeys.includes(key)){
            gltfMesh[key] = gltfRawData[key]
        }
    })
    return gltfMesh
}

export const defaultMaterialProperties={
    textureRepeat: {value:1.0, min:0.001, max:10.0, step:0.001},
    envMapIntensity : {value:1.0, min:0, max:2, step:0.01},
    clearcoat: {value:0.0, min:0, max:1, step:0.01},
    clearcoatRoughness: {value:0.0, min:0, max:1, step:0.01},
    ior : {value:1.5, min:1.0, max:2.333, step:0.01},
    reflectivity: {value:0.5, min:0, max:1, step:0.01},
    aoMapIntensity: {value:1.0, min:0, max:2, step:0.01},
    displacementScale : {value:0.0, min:-1, max:1, step:0.001},
    displacementBias  : {value:0, min:-10, max:10, step:0.01},
    emissiveIntensity: {value:1.0, min:0, max:5, step:0.01},
    metalness: {value:0.0, min:0, max:1, step:0.01},
    roughness: {value:1.0, min:0, max:1, step:0.01},
    transmission: {value:0.0, min:0, max:1, step:0.01},
    thickness:{value:0.0,min:0,max:5,step:0.01},
    normalScale: { value:1.0, min:0, max:5,step:0.1},
    sheen: {value:0.0, min:0, max:1, step:0.01},
    sheenRoughness: {value:1.0, min:0, max:1, step:0.01},
}

//656565. 16777215
//808080
// Colors are plain hex numbers — byte-identical to the old `new Color(0x...)`
// through deepClone/JSON since Color.toJSON() returns getHex().
export const materialDefaults = {clearcoat:0,clearcoatRoughness:0,
	ior:1.5,reflectivity:0.5,transmission:0,aoMapIntensity:1,
	displacementScale:1,displacementBias:0,emissiveIntensity:1.0,envMapIntensity:1.0,
	metalness:0.1,refractionRatio:0.98,roughness:0.6,side:0, transparent:false,
	color:0xFFFFFF, emissive:0x000000, sheenColor:0xFFFFFF, }

export const materialDefaults2 = {
    clearcoat:0,clearcoatRoughness:0,
    ior:1.5,reflectivity:0.5,transmission:0,aoMapIntensity:1,
    displacementScale:1,displacementBias:0,emissiveIntensity:1.0,envMapIntensity:1.0,
    metalness:0.0,refractionRatio:0.98,roughness:1.0,side:0, transparent:false,
    color:0xFFFFFF, emissive:0x000000, sheenColor:0xFFFFFF,
}

//export const materialDefaults = {side:2}

export const dimensionsToString = (dim,precision=2) => {
    return `${dim?.x.toFixed(precision)} x ${dim?.y.toFixed(precision)} x ${dim?.z.toFixed(precision)}mm`
}

export const dimensionsToStringFlipped = (dim,precision=2) => {
    return `${dim?.x.toFixed(precision)} x ${dim?.z.toFixed(precision)} x ${dim?.y.toFixed(precision)}mm`
}

export const calculateRepeatModifier = (physicalObject:PhysicalObjectInterface,scene=window.scene) => {
    // Handle null/undefined physicalObject (e.g., for background textures)
    if (!physicalObject) return 1;

    // DISABLED: Auto-repeat adjustment for dynamic/not_static objects
    if (physicalObject.type === 'dynamic' || physicalObject.type === 'not_static') {
        return 1;
    }
    
    if (!physicalObject?.originalScale || !physicalObject?.key) return 1;
    const threeJSObject = scene.children.filter( (child) => child.userData.key == physicalObject.key)[0]
    if (!threeJSObject?.scale) return 1;
    const meshTextureRepeat = (physicalObject.meshTextureRepeat || physicalObject?.mesh?.meshTextureRepeat) ?? 1
    
    // Original logic: texture repeat scales with object to maintain physical texture size
    const l = ((physicalObject.scale[0] / physicalObject.originalScale) * meshTextureRepeat )
    return l
}

export const convertGLBMeshToPhysicalObject = (mesh:GLBMesh,meshUrl,defaultColor=0xC5C5C5) => {
    if (!mesh) return;
    const scale = Array.isArray(mesh.scale) ? mesh.scale : [mesh.scale,mesh.scale,mesh.scale]
    const newPhysicalObject:PhysicalObjectInterface = {
        scale: scale,
        rotation: mesh.rotation ?? [0, 0, 0],
        position: [0, 0, 0],
        url: meshUrl ?? getUrlFromRef(mesh.web_sized_glb),
        key: Math.random(),
        name: 'Mesh',
        mergeGeo:false,
        version:mesh.version ?? 2,
        type: mesh?.meshType=='not_static' ? 'gltf' : mesh.meshType,
        isStaticObject: mesh.meshType=='static' || mesh.meshType=='gltf',
        meshTextureRepeat: mesh.meshTextureRepeat ?? 1
    }
    if (newPhysicalObject.isStaticObject===false) {
        if (!newPhysicalObject.materialData) {
            const materialProps:MaterialProperties = deepClone(materialDefaults)
            materialProps.metalness=0
            materialProps.roughness=1.0
            materialProps.color = defaultColor;
            newPhysicalObject.materialData = {materialProps: materialProps} as MaterialData
        }
    }
    return newPhysicalObject
}

export function getTimeAgo(time:number) {
    const diff = Date.now() - time
    const minutes = Math.floor(diff / 60000)
    if (minutes < 60) return minutes + ' minutes ago'
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return hours + ' hours ago'
    const days = Math.floor(hours / 24)
    return days + ' days ago'
}

export const createHash = (s) =>  s.split("").reduce(function(a,b){a=((a<<5)-a)+b.charCodeAt(0);return a&a},0);

export const createNewGLBFile = (mesh=MeshTypes.GLTF) => {
    return {
        meshType:mesh,meshTextureRepeat:1,isStaticObject:mesh!=MeshTypes.NOT_STATIC,
        rotation:[0,0,0],scale:[1,1,1],version:2,
        full_sized_glb:null as any,web_sized_glb:null as any,full_sized_vertices:0,
        web_sized_vertices:0,  full_file_size:0,web_file_size:0,
        rendered_image:null as any,
        thumbnail:null as any,
    }
}

/*
A few ugly things to move a stored gltf object to a physical object.
*/
export const convertGLTFMeshToPhyscialObject = (mesh:ProductInterface,sceneScaleFactor=1, gltfSize='web_sized_glb', defaultColor=0xC5C5C5) => {
    if (!mesh) return;
    // if (mesh.physicalObject) return mesh.physicalObject;
//    const scale = (Array.isArray(mesh.scale) ? mesh.scale : [mesh.scale,mesh.scale,mesh.scale]).map(x => x * sceneScaleFactor)
    const scale = Array.isArray(mesh.mesh?.scale) ? mesh.mesh?.scale : [mesh.mesh?.scale,mesh.mesh?.scale,mesh.mesh?.scale]
    const gltfMesh = produce(mesh, (draft:any)=> {
        delete draft.triangles;  delete draft.thumbnailSize; delete draft.id;
    })
    const newPhysicalObject:PhysicalObjectInterface | any= {
        ...gltfMesh,
        ...{type: gltfMesh.mesh?.web_sized_glb ? 'gltf' : 'shape',
            scale:scale,
            rotation:gltfMesh.mesh?.rotation,
            meshTextureRepeat: gltfMesh.mesh?.meshTextureRepeat,
            refId:mesh.id,
            position:[0,0,0],
            url: getUrlFromRef(gltfMesh.mesh?.[gltfSize]),
            key:Math.random()}
    }
    if (!newPhysicalObject.materialData) {
        const materialProps:MaterialProperties = deepClone(materialDefaults)
        materialProps.metalness=0
        materialProps.roughness=1.0
        materialProps.color = defaultColor;
        newPhysicalObject.materialData = {materialProps: materialProps} as MaterialData
    }
    return newPhysicalObject
}

export const roundBytesToMB = (bytes) => { return  Math.round( (bytes / (1024*1024)) * 100) / 100;}
export const round2 = (n) => { return(Math.round(n * 100)) / 100;}

export const  commonTypefaces = { 'Arial': 'Arial, sans-serif', 'Helvetica':'Helvetica, sans-serif',
'Georgia':'Georgia, serif','Courier New':'"Courier New", monospace','Lucida Console':'"Lucida Console", Monaco, monospace',
'Impact':'"Impact", Charcoal, sans-serif','Times New Roman':'"Times New Roman", Times, serif',
'Trebuchet':'"Trebuchet MS", Helvetica, sans-serif',
'Verdana':'"Verdana", Geneva, sans-serif','Montserat':'"Montserat", sans-serif'}

export const lineEndTypes = { 'None': 'none', 'Dot': 'dot', 'Dot Both Sides': 'dot-both-sides', 'Dot2': 'dot2', 'Dot2 Both Sides': 'dot2-both-sides',
'Dot3': 'dot3', 'Dot3 Both Sides': 'dot3-both-sides', 'Arrow': 'arrow', 'Arrow Both Sides': 'arrow-both-sides'}


export async function urlToImage(url) {
    return new Promise((resolve, reject) => {
        const img = new Image()
        img.crossOrigin = "Anonymous"
        img.onload = () => { resolve(img) }
        img.onerror = () => { console.log("I failed ", url); reject("Cannot load image"); }
        img.src = url
    })
}

function getCanvasScale(width, height) {
    const maxWidth = 8192;
    const maxHeight = 8192;
    let scale = 1;
    if (width > maxWidth || height > maxHeight) {
        scale = Math.min(maxWidth / width, maxHeight / height);
    }
    return scale;
}


export async function urlToCanvasWithoutCORS(url) {
    return new Promise((resolve, reject) => {
        if (!url || url.length  <1) { resolve(null); return; }
        if (url instanceof Blob == true || url instanceof File ==true) { resolve(url as Blob); return; }
        const img = new Image()
        img.crossOrigin = "Anonymous"
        img.onload = () => {
            const scale = getCanvasScale(img.width, img.height)
            const canvas = createCanvas(img.width*scale, img.height*scale)
            canvas.ctx.drawImage(img, 0, 0, img.width*scale, img.height*scale)
            resolve(canvas)
        }
        img.onerror = (e) => {
            console.log("error loading ", url,e);
            reject("Cannot load image");
        }
        img.src = url
    })
}
function getCORSImages(url) { return 'http://res.cloudinary.com/dck6qckup/image/fetch/w_1000/' + url; }

export async function urlToCanvas(url) {
	return new Promise((resolve, reject) => {
            const img = new Image()
            let isCORSImage = false
            img.crossOrigin = "Anonymous"
            img.onload = () => {
                const scale = getCanvasScale(img.width, img.height)
                const canvas = createCanvas(img.width*scale, img.height*scale)
                canvas.ctx.drawImage(img, 0, 0, img.width*scale, img.height*scale)
                resolve(canvas)
            }
            img.onerror = (err) => {
                if(!isCORSImage) {
                    img.src = getCORSImages(url)
                    isCORSImage = true
                }
                else {
                    console.warn("urlToCanvas: failed to load (CORS fallback exhausted)", url, err);
                    reject(err);
                }
            }
            img.src = url
	})
}

export function deepClone(obj) {  return JSON.parse(JSON.stringify(obj)) }

export async function getCanvas(src)  {
	return new Promise((resolve, reject) => {
		if (src instanceof Image ) { urlToCanvas(src.src).then(canvas=>resolve(canvas)).catch(err=>reject(err)) }
		else if (src instanceof File) {
			const reader = new FileReader();
			reader.onload = () => {
				const dataURL = reader.result;
				urlToCanvas(dataURL).then( (canvas) =>  resolve(canvas) )
			}
			reader.readAsDataURL(src)
		}
		else if (typeof(src)=='string') {  urlToCanvas(src).then( (canvas) => resolve(canvas)).catch(err=>reject(err)) }
		else if (src.tagName=='CANVAS') { resolve(src)  }
	})
}


export async function resizeImage(img,maxWidth=600,maxHeight=600,imgName) {
	return new Promise((resolve, reject) => {
        getCanvas(img).then( (imgCanvas:any) => {
			let ratio = 1;
            let widthRatio = 1, heightRatio = 1;

            if(imgCanvas.width > maxWidth) widthRatio = maxWidth / imgCanvas.width;
			if(imgCanvas.height > maxHeight) heightRatio = maxHeight / imgCanvas.height;

            ratio = widthRatio < heightRatio ? widthRatio : heightRatio;

			const canvasCopy = createCanvas(imgCanvas.width * ratio, imgCanvas.height * ratio);
            canvasCopy.id =imgName? imgName: img.name
			canvasCopy.ctx.drawImage(imgCanvas,0,0,imgCanvas.width,imgCanvas.height,0,0,canvasCopy.width,canvasCopy.height);

            resolve(canvasCopy);

			// const i = new Image()
			// i.onload = () => resolve(i)
			// i.src = canvasCopy.toDataURL(toPath)
		})
        .catch((err) => {
            reject(err);
        })
	})
}
export async function convertImageToBlob(canvas, toPath){
     const extension = toPath.match('image/png')?'.png':'.jpg';
	return new Promise((resolve, reject) => {
        canvas.toBlob(function(blob) {
            blob.extension = extension;
            resolve(blob);
        },toPath)
    })
}
export async function uploadImage(name,img,toPath,uid, description='', brand='', url='', category?:string){
    return new Promise((resolve, reject) => {

        const data:any = {
            name:name,
            uid:uid,
            createdAt:Date.now(),
            updatedAt:Date.now(),
            id:camelize(name) + '_' + generateID(),
            description: description,
            brand: brand,
            url: url,
            ...(category && { category }),
        }

        convertImageToBlob(img,toPath)
        .then((imageBlob) => {
            uploadAndSave(data.id, 'userImages', data.uid, data, {file:imageBlob})
                .then(result=>{
                    resolve(result);
                })
                .catch(reject)
              })
        .catch(reject)
        })
}
    export const checkFileType = (file) => {
        const fileType = file.type.toLowerCase();
        const isImage = fileType.startsWith('image/')
        const toPath = fileType.match('image/png')?'image/png':'image/jpeg';
        const fileName = file.name.replace(/[&\/\\#,+()$~%'":*?<>{}@!^]/g, '');
        return {isImage:isImage,toPath:toPath,fileName:fileName}
    }
    export const checkWebsiteFileType = (html) => {
        const imageSrc = new DOMParser().parseFromString(html, "text/html").querySelector('img')?.src
        if(imageSrc?.includes('mattoboard')) return null
        if(imageSrc){
            const isImage = true
            const extension = (imageSrc?.includes("jpeg")|| imageSrc?.includes("jpg"))?'jpeg':imageSrc?.includes("png")?'png':'jpeg';
            const toPath = extension.match('png')?'image/png':'image/jpeg';
            const imageAlt = new DOMParser().parseFromString(html, "text/html").querySelector('img')?.alt
            let fileName = imageAlt ? imageAlt :  imageSrc.split("/")[(imageSrc.split("/")).length -1]
            fileName = fileName?.replace(/[&\/\\#,+()$~%'":*?<>{}@!^]/g, '')+ '.' + extension;
            return {src:imageSrc,isImage:isImage,toPath:toPath,fileName:fileName}
        }
        return null
    }
    export { hashCode, unflattenObject } from 'ui'

    export const material50cm =     {"description":"","files":{"large_color":"materials/0gcVfuJkctTs0p7IRBVOh4WDqYw2/5cm_s9rhqdeid3/color_large_v2.jpg","color_original":"materials/0gcVfuJkctTs0p7IRBVOh4WDqYw2/5cm_s9rhqdeid3/1641555717142_color_original","small_color":"materials/0gcVfuJkctTs0p7IRBVOh4WDqYw2/5cm_s9rhqdeid3/color_small_v2.jpg","thumbnail":"materials/0gcVfuJkctTs0p7IRBVOh4WDqYw2/5cm_s9rhqdeid3/color_cent_v2.jpg","cent_color":"materials/0gcVfuJkctTs0p7IRBVOh4WDqYw2/5cm_s9rhqdeid3/color_cent_v2.jpg","medium_color":"materials/0gcVfuJkctTs0p7IRBVOh4WDqYw2/5cm_s9rhqdeid3/color_medium_v2.jpg","tiny_color":"materials/0gcVfuJkctTs0p7IRBVOh4WDqYw2/5cm_s9rhqdeid3/color_tiny_v2.jpg"},"active":true,"tags":[],"id":"5cm_s9rhqdeid3","materialCategory":["Carpet"],"supplier":"","updatedAt":1641555717141,"name":"5cm","category":null,"materialProps":{"thickness":0,"metalness":0,"displacementScale":0,"sheenRoughness":1,"envMapIntensity":1,"ior":1.5,"normalScale":[0.4,0.4],"aoMapIntensity":1,"displacementBias":0,"color":"#ffffff","clearcoat":0,"textureRepeat":1,"transmission":0,"wireframe":false,"emissive":"#000000","roughness":1,"sheenTint":"#ffffff","sheen":0,"reflectivity":0.5},"visible":false,"scale":0.06,"isMaterialChangeable":null}

    export const IsUrlShow = () => {
        const url = window.location.href
        if(url.includes('show/')) return true;
        if(url.includes('metasku/')) return true;
        return false;
    }

    const hasValidSupplier = (supplier: string | undefined): supplier is string => {
        return !!supplier && supplier !== 'No Supplier' && supplier.trim() !== '';
    };

    export const getProductDisplayName = (product:ProductInterface) => {
        if (!product) return 'Unknown'
        let displayName = product.name
        
        // Add supplier if it exists, is valid, and name doesn't already start with supplier
        if (hasValidSupplier(product.supplier) && !product.name.startsWith(product.supplier)) {
            if (product.supplier.indexOf('BEHR') >= 0) {
                displayName = product.supplier + ' ' + product.name
            } else if (product.supplier.indexOf('180fx') >= 0) {
                displayName = "Formica - 180fx® - " + product.name
            } else {
                displayName = product.supplier + ' - ' + product.name
            }
        }
        
        // Handle variantName if it exists
        if (product.hasOwnProperty('variantName')) {
            if (product.variantName !== null && product.variantName !== undefined && product.variantName.trim() !== '') {
                displayName = displayName + ' - ' + product.variantName
            }
        }
        
        return displayName
    }

    export const getMaterialThumbnail = (material) => {
        if ( material?.materialData?.renderedImage?.length > 5) {
            //brin back rendered image!
           return cloudflareCDN(material.materialData.renderedImage, 'height=120, width=120,format=auto')
        }
        else if (material?.materialData?.files?.thumbnail)  {
            return getUrlFromRef(material.materialData.files.thumbnail,true)
        }
        else {
            let renderedImage= material?.materialData?.files?.color_original
            if(!renderedImage) renderedImage = material?.materialData?.renderedImage;
            // if(renderedImage) return cloudflareCDN(renderedImage, 'height=120, width=120,format=auto')
            if(renderedImage) {
                if(material?.materialData?.aspectRatio > 1) return cloudflareCDN(renderedImage, 'height=120, format=auto')
                else if(material?.materialData?.aspectRatio < 1) return cloudflareCDN(renderedImage, 'width=120, format=auto')
                else return cloudflareCDN(renderedImage, 'height=120, width=120, format=auto')
            }
            else return false;
        }
	}
    export const getProductThumbnail = (gltf) => {
        if (gltf.mesh?.rendered_image) return cloudflareCDN(gltf.mesh?.rendered_image,'height=120,format=auto')
        if (gltf.mesh?.thumbnail) return cloudflareCDN(gltf.mesh?.thumbnail,'height=120,format=auto')
		const thumbnail =  gltf.files?.thumbnail
        if (!thumbnail) return undefined;
        if (gltf.isDynamic) return thumbnail;
	    return getUrlFromRef(thumbnail)
	}

    /**
     * Thumbnail for any ProductInterface doc: static objects (gltf, accessories,
     * hardware, fixed materials) render via getProductThumbnail, everything else
     * via getMaterialThumbnail, falling back to the first refImage (off-catalog
     * FF&E products carry ONLY refImages). Never throws — returns '' when the
     * product carries no usable image refs.
     */
    export const productOrMaterialThumbnail = (product) => {
        try {
            const isStatic = product.isStaticObject &&
                (product.type === 'gltf' ||
                 [ProductType.ACCESSORY, ProductType.HARDWARE, ProductType.FIXEDMATERIAL].includes(product.productType))
            return (isStatic ? getProductThumbnail(product) : getMaterialThumbnail(product))
                || (product?.refImages?.[0] ? cloudflareCDN(product.refImages[0], 'height=120,format=auto') : '')
        } catch {
            return ''
        }
    }

    /**
     * Gets the thumbnail URL for mesh/object products (hardware, accessories, fixed materials).
     * Used in search results and collection displays.
     * Priority: mesh.rendered_image → files.thumbnail → mesh.thumbnail
     */
    export const getMeshThumbnailUrl = (gltf) => {
        if (gltf.mesh?.rendered_image) return cloudflareCDN(gltf.mesh?.rendered_image,'height=120,format=auto')
        if (gltf.files?.thumbnail) { return getUrlFromRef(gltf.files?.thumbnail) }
        if (gltf.mesh?.thumbnail) { return cloudflareCDN(gltf.mesh?.thumbnail,'height=120,format=auto') }
        return undefined;
    }

    export const getMaterialThumbnailFromMaterialData = (materialData) => {
        if ( materialData?.renderedImage?.length > 5) {
            //bring back rendered image!
           return cloudflareCDN(materialData.renderedImage, 'height=120, width=120,format=auto')
        }
        else if (materialData?.files?.thumbnail)  {
            return getUrlFromRef(materialData.files.thumbnail,true)
        }
        else {
            let renderedImage= materialData?.files?.color_original
            if(!renderedImage) renderedImage = materialData?.renderedImage;
            // if(renderedImage) return cloudflareCDN(renderedImage, 'height=120, width=120,format=auto')
            if(renderedImage) {
                if(materialData?.aspectRatio > 1) return cloudflareCDN(renderedImage, 'height=120, format=auto')
                else if(materialData?.aspectRatio < 1) return cloudflareCDN(renderedImage, 'width=120, format=auto')
                else return cloudflareCDN(renderedImage, 'height=120, width=120, format=auto')
            }
            else return false;
        }
	}

    // Thumbnail for a board object of any kind (used by the multi-select summary).
    // Never throws; undefined when nothing usable exists (canvas labels, missing refs).
    export const getPhysicalObjectThumbnail = (physicalObject): string | undefined => {
        try {
            if (!physicalObject) return undefined
            if (physicalObject.type === 'canvas') return undefined
            if (physicalObject.type === 'paint') {
                return getMaterialThumbnailFromMaterialData(physicalObject.paintMaterialsData?.[0]) || getProductThumbnail(physicalObject) || undefined
            }
            if (physicalObject.type === 'dynamic') {
                return getImageThumbnail(physicalObject) || getMaterialThumbnailFromMaterialData(physicalObject.materialData) || undefined
            }
            if (physicalObject.isStaticObject) return getProductThumbnail(physicalObject) || undefined
            return getMaterialThumbnailFromMaterialData(physicalObject.materialData) || getProductThumbnail(physicalObject) || undefined
        } catch {
            return undefined
        }
    }

    export const getImageThumbnail = (shapeshift) => {
        if (shapeshift?.dynamicMaterialProps?.files)  {
            return getUrlFromRef(shapeshift.dynamicMaterialProps?.files,true)
        }
        else return null;
	}

    function isWriteable(obj) {
        try {
            obj.test = 1; delete obj.test;
            return true;
        }
        catch (e) { return false; }
    }

    function removeColorOriginal(materialData) {
            delete materialData.files.color_original;
        return materialData;
    }

    // Default board background — keep in sync with LoadScene.setDefaultBackgroundTexture.
    export const DEFAULT_BACKGROUND_PRODUCT_ID = 'Material-05252'
    // Background Repeat for it. Its stored repeat (2.006) is its tiling on objects; + 10 lands at ~12.
    const DEFAULT_BACKGROUND_TEXTURE_REPEAT = 10

    // Shape a raw productsV2 record into a backgroundTexture. Extracted from
    // setBackgroundTexture so callers that persist a board (createBoardInProject)
    // build the same shape without the store setter.
    export const buildBackgroundTexture = (firebaseRecord): ProductInterface => {
        const backgroundTexture: ProductInterface = {
            id: firebaseRecord.id,
            name: firebaseRecord.name,
            productType: firebaseRecord.productType,
            objectStatus: firebaseRecord.objectStatus,
            updatedAt: firebaseRecord.updatedAt,
            materialData: firebaseRecord.materialData,
            isStaticObject: false
        }
        if(firebaseRecord.product_group_id && firebaseRecord.product_group_id != firebaseRecord.id) {
            backgroundTexture.product_group_id = firebaseRecord.product_group_id
        }
        if(backgroundTexture.materialData?.materialProps?.textureRepeat) {
            backgroundTexture.materialData.materialProps.textureRepeat = backgroundTexture.id == DEFAULT_BACKGROUND_PRODUCT_ID
                ? DEFAULT_BACKGROUND_TEXTURE_REPEAT
                : backgroundTexture.materialData.materialProps.textureRepeat + 10
        }
        if(backgroundTexture.objectStatus==ObjectStatusType.USER_CREATED && backgroundTexture.productType=='paint' && backgroundTexture?.materialData?.files?.color_original) {
            backgroundTexture.materialData = removeColorOriginal(structuredClone(firebaseRecord.materialData));
            backgroundTexture.materialData.renderedImage = firebaseRecord.materialData.files.color_original;
        }
        return backgroundTexture
    }

    export const setBackgroundTexture = (firebaseRecord, setProjectData) => {
        setProjectData(state=> { state.backgroundTexture = buildBackgroundTexture(firebaseRecord) } )
    }

    // ImageItem spreads materialData conditionally, so fixtures, meshes and user
    // images arrive here without it. Both handlers below write into
    // materialData.*, so they can't proceed (MATTOBOARD-62X).
    const NOT_A_MATERIAL_MESSAGE = "This item can't be applied as a material."

    export const handleBackgroundChange=(record,getCurrentThreeJSObject,getCurrentSelectedPhysicalObject,setProjectData,updatePhysicalObject,selectedMaterialId=0) => {
        let firebaseRecord = record
        if (!firebaseRecord) return;
        if (!firebaseRecord.materialData) {
            useMattoState.getState().setSnackbarMessage(NOT_A_MATERIAL_MESSAGE)
            return;
        }
        if (firebaseRecord?.materialData.materialProps==null) {
            firebaseRecord.materialData.materialProps = deepClone(materialDefaults)
        }
        if (isWriteable(firebaseRecord.materialData.materialProps)==false) {
            firebaseRecord = deepClone(firebaseRecord)
        }
        if (!firebaseRecord.materialData?.materialProps?.textureRepeat) {
            firebaseRecord.materialData.materialProps.textureRepeat = 1
        }
        const threeObject = getCurrentThreeJSObject ? getCurrentThreeJSObject() : null

        if (threeObject==null) {
            setBackgroundTexture(firebaseRecord,setProjectData);
        }
    }

    // Pure core of handleMeshChange: the object with `firebaseRecord` applied, or
    // null when the record and object are incompatible (paint ↔ non-paint).
    export const applyMaterialRecordToObject = (firebaseRecord, selectedPhysicalObject, selectedMaterialId=0) => {
        if (!firebaseRecord || !selectedPhysicalObject) return null;
        if (selectedPhysicalObject?.type=='paint' &&  firebaseRecord?.productType != 'paint') return null;
        if (firebaseRecord?.productType == 'paint' && selectedPhysicalObject?.type != 'paint') return null;
        const isPaint = selectedPhysicalObject?.type=='paint'

        if (isPaint) {
            return produce(selectedPhysicalObject, draft=> {
                if (firebaseRecord.materialData?.paintMaterials?.length > 0) draft.materialData = firebaseRecord.materialData
                else {
                    // //lets get r id of this at some point.
                    if (!draft.paintMaterials) draft.paintMaterials = [];
                    draft.paintMaterials[selectedMaterialId]=firebaseRecord.materialData.materialProps
                    if (!draft.paintMaterialsData) draft.paintMaterialsData = [];
                    if (!draft.paintMaterialsData[selectedMaterialId]) draft.paintMaterialsData[selectedMaterialId]={preset:firebaseRecord.materialData.preset,id:null,name:null,renderedImage:null};
                    // The slot's finish (Flat / Eggshell / Satin…, Variants.tsx) is the
                    // designer's setting: a new paint keeps it and only supplies a
                    // preset to a slot that has none.
                    if (firebaseRecord?.materialData?.preset && !draft.paintMaterialsData[selectedMaterialId].preset) draft.paintMaterialsData[selectedMaterialId].preset = firebaseRecord.materialData.preset;
                    if (firebaseRecord?.materialData?.id || firebaseRecord?.id) draft.paintMaterialsData[selectedMaterialId].id = firebaseRecord.materialData.id || firebaseRecord.id ;
                    if (firebaseRecord?.materialData?.name || firebaseRecord?.name) draft.paintMaterialsData[selectedMaterialId].name = firebaseRecord.name || firebaseRecord.materialData.name;
                    if (firebaseRecord?.materialData?.renderedImage) draft.paintMaterialsData[selectedMaterialId].renderedImage  = firebaseRecord.materialData.renderedImage;
                    if (firebaseRecord?.objectStatus) draft.paintMaterialsData[selectedMaterialId].objectStatus = firebaseRecord.objectStatus;
                    if (firebaseRecord?.supplier) draft.paintMaterialsData[selectedMaterialId].supplier = firebaseRecord.supplier;
                    if (firebaseRecord?.category) draft.paintMaterialsData[selectedMaterialId].category = firebaseRecord.category;
                    if (firebaseRecord?.productLink) draft.paintMaterialsData[selectedMaterialId].productLink = firebaseRecord.productLink;
                    if (firebaseRecord?.objectStatus==ObjectStatusType.USER_CREATED) {
                        draft.paintMaterialsData[selectedMaterialId].userCreated  = true;
                    }
                    else {
                        draft.paintMaterialsData[selectedMaterialId].userCreated  = false;
                    }
                }
            })
        }
        // Regular material application (applies to all meshes)
        return produce(selectedPhysicalObject, draft=> {
            if (draft.materialData==null) draft.materialData={id: null,name:null,files:null,materialProps:null,renderedImage:null};
            if (firebaseRecord?.materialData?.id) draft.materialData.id = firebaseRecord.materialData.id;
            if (firebaseRecord?.uid) draft.materialData.uid = firebaseRecord.uid;
            if (firebaseRecord?.id) draft.materialData.id = firebaseRecord.id;
            if (firebaseRecord?.materialData?.aspectRatio) draft.materialData.aspectRatio = firebaseRecord.materialData.aspectRatio;
            if (firebaseRecord?.materialData?.files) draft.materialData.files = firebaseRecord.materialData.files;
            if (firebaseRecord?.materialData?.materialProps) draft.materialData.materialProps = firebaseRecord.materialData.materialProps;
            if (firebaseRecord?.materialData?.name) draft.materialData.name = firebaseRecord.materialData.name;
            if (firebaseRecord?.materialData?.renderedImage) draft.materialData.renderedImage = firebaseRecord.materialData.renderedImage;
            if (firebaseRecord?.product_group_id) draft.product_group_id = firebaseRecord.product_group_id;
            else if (firebaseRecord?.name) draft.materialData.name = firebaseRecord.name;
            if (firebaseRecord.category) draft.materialData.category = firebaseRecord.category;
            if (firebaseRecord?.product_group_id) draft.product_group_id = firebaseRecord.product_group_id;
            if (firebaseRecord?.hasOwnProperty('variantName')) draft.variantName = firebaseRecord.variantName; // for V2
            if (firebaseRecord?.hasOwnProperty('supplier')) draft.supplier = firebaseRecord.supplier; // for V2
            if (firebaseRecord?.optimal_object_category) draft.optimal_object_category = firebaseRecord.optimal_object_category;
        });
    }

    // Multi-selection: apply the record to every selected object that accepts
    // materials, in ONE store write. Paint is per-object (its own picker), so a
    // paint record asks for a single selection.
    export const applyMaterialToSelection = (firebaseRecord, keys, selectedMaterialId=0) => {
        const store = usePhysicalObjects.getState()
        if (firebaseRecord?.productType == 'paint') {
            useMattoState.getState().setSnackbarMessage("Select a single paint object to apply paint")
            return;
        }
        const targets = keys.map((k) => store.getSelectedPhysicalObject(k)).filter(isMaterialEditable)
        if (!targets.length) {
            useMattoState.getState().setSnackbarMessage("None of the selected objects accept materials")
            return;
        }
        // Each object gets its own copy so later per-object edits can't alias.
        const updated = targets
            .map((t) => applyMaterialRecordToObject(deepClone(firebaseRecord), t, selectedMaterialId))
            .filter(Boolean)
        store.updatePhysicalObjects(updated)
        if (updated.length) recordBoardUse(productRefFromRecord(firebaseRecord), 'apply')
        trackGroupAction('material_apply', targets.length, { skipped: keys.length - targets.length })
        if (targets.length < keys.length) {
            useMattoState.getState().setSnackbarMessage(`Material applied to ${targets.length} of ${keys.length} selected objects`)
        }
    }

    export const handleMeshChange=(record,getCurrentThreeJSObject,getCurrentSelectedPhysicalObject,setProjectData,updatePhysicalObject,selectedMaterialId=0) => {
        let firebaseRecord = record
        if (!firebaseRecord) return;
        if (!firebaseRecord.materialData) {
            useMattoState.getState().setSnackbarMessage(NOT_A_MATERIAL_MESSAGE)
            return;
        }
        if (firebaseRecord?.materialData.materialProps==null) {
            firebaseRecord.materialData.materialProps = deepClone(materialDefaults)
        }
        if (isWriteable(firebaseRecord.materialData.materialProps)==false) {
            firebaseRecord = deepClone(firebaseRecord)
        }
        if (!firebaseRecord.materialData?.materialProps?.textureRepeat) {
            firebaseRecord.materialData.materialProps.textureRepeat = 1
        }
        // Multi-selection → bulk path (no call-site changes needed).
        const selectedKeys = usePhysicalObjects?.getState?.()?.getSelectedKeys?.() ?? []
        if (selectedKeys.length > 1) {
            applyMaterialToSelection(firebaseRecord, selectedKeys, selectedMaterialId)
            return;
        }
        const threeObject = getCurrentThreeJSObject ? getCurrentThreeJSObject() : null
        const selectedPhysicalObject= getCurrentSelectedPhysicalObject()

        if (selectedPhysicalObject?.type=='paint' &&  firebaseRecord?.productType != 'paint') return;
        if (firebaseRecord?.productType == 'paint' && selectedPhysicalObject != null &&  selectedPhysicalObject?.type != 'paint') return;

        //background object
        if (threeObject==null) {
            return;
        }
        const updatedObject = applyMaterialRecordToObject(firebaseRecord, selectedPhysicalObject, selectedMaterialId)
        if (updatedObject) {
            updatePhysicalObject(updatedObject)
            recordBoardUse(productRefFromRecord(firebaseRecord), 'apply')
        }
    }
    export interface ComputeMapsInput {
        id:string;
        normalMap: string;
        albedoMap: string;
    }

    export async function computeMaps(img, id: string, currentUserUid: string): Promise<ComputeMapsInput> {
        const storageURL = useStaging ? 'mattoboard-staging.appspot.com' : 'mattoboard-b8284.appspot.com'
        const normalMapUrl = useStaging ? 'https://us-central1-mattoboard-staging.cloudfunctions.net/generate_maps' : 'https://us-central1-mattoboard-b8284.cloudfunctions.net/generate_maps';
        const response = await fetch(`https://storage.googleapis.com/${storageURL}/userImages/${currentUserUid}/${id}/normal.jpg`)

        if (response.status==200) {
            const computerMapsInput: ComputeMapsInput = {
                id: id,
                normalMap: `https://storage.googleapis.com/${storageURL}/userImages/${currentUserUid}/${id}/normal.jpg`,
                albedoMap: `https://storage.googleapis.com/${storageURL}/userImages/${currentUserUid}/${id}/albedo.jpg`,
            }
            return computerMapsInput
        }
        const inputData = {
            id: id,
            uid: currentUserUid,
            image_url: img?.src ? img.src : img,
        };
        try {
            const resp = await fetch(normalMapUrl, {
                method: 'POST',
                headers: {'Accept': 'application/json','Content-Type': 'application/json'},
                body: JSON.stringify(inputData)
            });
            const data = await resp.json();
            return data as ComputeMapsInput
        } catch (error) {
            console.error("Failed to fetch normal map", error);
            return {
                id: id,
                normalMap: '',
                albedoMap: img?.src ? img.src : img,
            }
        }
    }
    function decimalHexToRGB(decimalHex) {
        const hexString = decimalHex.toString(16).padStart(6, '0');
        const r = parseInt(hexString.substr(0, 2), 16);
        const g = parseInt(hexString.substr(2, 2), 16);
        const b = parseInt(hexString.substr(4, 2), 16);
        return { r, g, b };
      }

      function rgbToDecimalHex(rgb) {
        return parseInt(`${rgb.r.toString(16).padStart(2, '0')}${rgb.g.toString(16).padStart(2, '0')}${rgb.b.toString(16).padStart(2, '0')}`, 16);
      }




    export const getHex = (color) =>  isNaN(color) ? color :  '#' + (color+0x10000).toString(16).substr(-3).toUpperCase();
    export const hexToInt = (color:string) =>  parseInt(color.replace('#',''),16)


    export function generateHexCodeArray(targetDecimalHex) {
    const targetRGB = decimalHexToRGB(targetDecimalHex);
    const hexCodeArray: number[] = [];
    const range = 16; // numCodes;

    for (let i = -range; i <= range; i++) {
        for (let j = -range; j <= range; j++) {
            for (let k = -range; k <= range; k++) {
                const newRGB = {
                    r: Math.min(255, Math.max(0, targetRGB.r + i)),
                    g: Math.min(255, Math.max(0, targetRGB.g + j)),
                    b: Math.min(255, Math.max(0, targetRGB.b + k)),
                };

                const newHexCode = rgbToDecimalHex(newRGB);
                hexCodeArray.push(newHexCode);
            }
        }
    }

    return hexCodeArray;
    }

    export const encodeHTML = (s) => s?.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
    export const setProfileFirstAction = (action:ProfileFirstActions, uid?:string|null) => {
        return new Promise((resolve, reject) => {
            if (window.gtag) {
                window.gtag("event", "first_action_"+action, { value: action});
            }
            trackEvent(action);
            // Meta activation, once per user. Only activation call sites pass a uid.
            if (action === ProfileFirstActions.DRAG_MATERIAL) trackMetaActivationOnce(uid, "drag_material");
            else if (action === ProfileFirstActions.CLICK_MATERIAL) trackMetaActivationOnce(uid, "click_material");
            else if (action === ProfileFirstActions.SAVE_BUTTON) trackMetaActivationOnce(uid, "save_board");
            resolve(true)
        })
    }

    export { gtagViewItemEvent } from './gaViewItem';
    export const gtagSearchEvent = (searchValue) => {
        if (window.gtag)  window.gtag("event", "search", {
            search_term: searchValue
        });
    }
    export const gtagFacetSearchEvent = (attribute, facetValue) => {
        if (window.gtag)  window.gtag("event", "Facetsearch", {
            facet_attribute: attribute,
            facet_search: facetValue
        });
    }

    export const gTagBrandClickEvent = (supplierID, supplier, destination, url) => {
        if(window.gtag) {
            window.gtag('event', 'SupplierClick', {
                brand_id: supplierID,
                brand_name: supplier,
                brand_click_type: destination,
                brand_click_url: url
            });
        }
    }
    export const gtagCuratedImagesDragEvent = (collectionID, collectionName) => {
        if (window.gtag)  window.gtag("event", "curated_images_drag", {
            curated_images_collection_id: collectionID,
            curated_images_collection_name: collectionName
        });
    }

    export const fetchData = async (requestData, url, extraHeaders = {}, { rethrow = false } = {}) => {
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                ...extraHeaders
                },
                body: JSON.stringify(requestData)
            });

            if (!response.ok) {
                // Surface the server's error message (e.g. the price-experiment
                // "please refresh" rejection) instead of a generic failure.
                const errorBody = await response.json().catch(() => null);
                throw Object.assign(new Error(errorBody?.error?.message || 'API request failed'), { status: response.status });
            }

            const responseData = await response.json();
            return responseData;

        } catch (error) {
            console.error('Error fetching data:', error);
            if (rethrow) throw error;
        }
    }

    export const getThumbnailFromTypesense = async (ids) => {
        const result = await fetch(`https://`+ typesenseHost + `/collections/productsV2/documents/search?q=*&per_page=${ids.length}&filter_by=id:[${ids}]`, {
            method: "GET",
            headers: {"Content-type": "application/json", "X-TYPESENSE-API-KEY": typesenseApiKey}
        });
        const jsonResponse = await result.json();
        return jsonResponse;
    }

export function base64ToBlob(base64: string, contentType: string) {
    const byteCharacters = atob(base64);
    const byteArrays: any = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);

      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: contentType });
  }



export const escapeSpecialCharacters = (url: string | undefined | null): string => {
	if (!url || typeof url !== 'string') return '';

	// Handle parentheses (CRITICAL for special chars)
	url = url.replace(/\(/g, "%28").replace(/\)/g, "%29");

	// Handle quotes and spaces
	url = url.replace(/'/g, '%27');
	url = url.replace(/ /g, '%20');

	// Check if additional URI encoding needed (prevents double-encoding)
	const needsEncoding = (str: string): boolean => {
		// decodeURI deliberately preserves reserved-character escapes (%2F, %3A,
		// %26 …), so `decodeURI(str) === str` reports "unencoded" for a URL that is
		// already encoded and we encode it a second time: %2F -> %252F, which the
		// image CDN then 404s on as a literal object name. Any %XX means encoded.
		if (/%[0-9A-Fa-f]{2}/.test(str)) return false;
		try {
			return decodeURI(str) === str;
		} catch {
			return false;
		}
	};

	return needsEncoding(url) ? encodeURI(url) : url;
};

/**
 * Converts a string to title case (capitalizes the first letter of each word)
 * @param str - The string to convert
 * @returns The string in title case
 */
export const toTitleCase = (str: string): string => {
	if (!str) return str;
	return str.replace(/\b\w+/g, word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
};

